import { Installments } from 'components/Cart/OrderSummary/types';
import { getProductPriceDetail } from 'hooks/useDCS.utils';
import { SUBSCRIPTION_STATE } from 'hooks/useSubscriptionsState';
import { IProduct, ISoftwarePlan } from 'services/dcs/bundle';

type ProductQuantityAndMultiDeviceFlag = { quantity: number; isIncludedInMultiDevicePricing: boolean };

const additionalProductReducer = (sum: number, item: ProductQuantityAndMultiDeviceFlag) =>
  item.isIncludedInMultiDevicePricing ? sum + item.quantity : sum;

export const getSoftwarePriceWithAdditionalProducts = (
  products: ProductQuantityAndMultiDeviceFlag[],
  additionalDevicePrice: number,
  softwareBaseMonthlyPrice: number,
) => {
  const devicesQuantityIncludedInSoftwarePricing = products.reduce(additionalProductReducer, 0);
  const additionalDevicesQuantity = Math.max(0, devicesQuantityIncludedInSoftwarePricing - 1);

  return softwareBaseMonthlyPrice + additionalDevicesQuantity * additionalDevicePrice;
};

export const getSoftwareMonthlyPricing = (
  dcsId: string,
  softwarePlans: ISoftwarePlan[],
  productsWithQuantityAndMultiDeviceFlag: ProductQuantityAndMultiDeviceFlag[],
) => {
  const filteredSoftwarePlan = softwarePlans?.find(plan => dcsId === plan.id);
  return getSoftwarePriceWithAdditionalProducts(
    productsWithQuantityAndMultiDeviceFlag,
    filteredSoftwarePlan?.pricing?.additionalDevicePrice?.value ?? 0,
    filteredSoftwarePlan?.pricing?.monthlyPrice.value ?? 0,
  );
};

export const getProductBasePrice = (product: IProduct) => {
  const basePrice = product.pricing.find(pricing => pricing.installmentPlan.code === Installments.P);
  return basePrice?.price.value ?? 0;
};

export const getProductsTotalUpfrontPrice = (dcsIds: string[], products?: IProduct[]) => {
  const matchingProducts = products?.filter(product => dcsIds.includes(product.id)) || [];
  return matchingProducts.reduce((totalPrice, product) => totalPrice + getProductBasePrice(product), 0);
};

export const getProductsWithoutPlansTotalUpfrontPrice = (dcsIds: string[], products?: IProduct[]) => {
  const subscriptionPlanCodes = ['SL36', 'SL48'];

  const matchingProducts =
    products?.filter(
      product =>
        dcsIds.includes(product.id) &&
        !product.pricing.some(pricing => subscriptionPlanCodes.includes(pricing.installmentPlan.code)),
    ) || [];

  return matchingProducts.reduce((totalPrice, product) => totalPrice + getProductBasePrice(product), 0);
};

export const getProductsTotalSubscriptionPrice = (
  dcsIds: string[],
  subscriptionsState: SUBSCRIPTION_STATE,
  products?: IProduct[],
) => {
  const matchingProducts = products?.filter(product => dcsIds.includes(product.id)) || [];
  return matchingProducts.reduce(
    (totalSubscriptionPrice, product) =>
      totalSubscriptionPrice + getProductSubscriptionPrice(subscriptionsState, product),
    0,
  );
};

export const getProductSubscriptionPrice = (subscriptionsState: SUBSCRIPTION_STATE, product?: IProduct) => {
  const productPriceDetail = getProductPriceDetail(product);
  if (subscriptionsState === SUBSCRIPTION_STATE.ENABLED_36_MONTH)
    return productPriceDetail?.installmentPlans.SL36?.value ?? 0;
  if (subscriptionsState === SUBSCRIPTION_STATE.ENABLED_48_MONTH)
    return productPriceDetail?.installmentPlans.SL48?.value ?? 0;
  return 0;
};
